interface PerformanceMetric {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  metadata?: Record<string, any>;
}

class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetric> = new Map();
  private enabled: boolean = false; // Disabled to reduce console noise

  start(name: string, metadata?: Record<string, any>): void {
    if (!this.enabled) return;
    
    const metric: PerformanceMetric = {
      name,
      startTime: performance.now(),
      metadata
    };
    
    this.metrics.set(name, metric);
    // console.log(`⏱️ [PERF START] ${name}`, metadata || '');
  }

  end(name: string): number | null {
    if (!this.enabled) return null;
    
    const metric = this.metrics.get(name);
    if (!metric) {
      // console.warn(`Performance metric '${name}' not found`);
      return null;
    }
    
    metric.endTime = performance.now();
    metric.duration = metric.endTime - metric.startTime;
    
    const durationStr = metric.duration.toFixed(2);
    const color = metric.duration > 1000 ? '🔴' : metric.duration > 500 ? '🟡' : '🟢';
    
    // console.log(`${color} [PERF END] ${name}: ${durationStr}ms`, metric.metadata || '');
    
    return metric.duration;
  }

  measure(name: string, fn: () => void): void {
    if (!this.enabled) {
      fn();
      return;
    }
    
    this.start(name);
    fn();
    this.end(name);
  }

  async measureAsync<T>(name: string, fn: () => Promise<T>): Promise<T> {
    if (!this.enabled) {
      return fn();
    }
    
    this.start(name);
    try {
      const result = await fn();
      this.end(name);
      return result;
    } catch (error) {
      this.end(name);
      throw error;
    }
  }

  clear(): void {
    this.metrics.clear();
  }

  getMetrics(): PerformanceMetric[] {
    return Array.from(this.metrics.values());
  }

  logSummary(): void {
    if (!this.enabled) return;
    
    const metrics = this.getMetrics();
    if (metrics.length === 0) return;
    
    // Performance summary logging disabled
    // console.group('📊 Performance Summary');
    // const completed = metrics.filter(m => m.duration !== undefined);
    // const total = completed.reduce((sum, m) => sum + (m.duration || 0), 0);
    // completed
    //   .sort((a, b) => (b.duration || 0) - (a.duration || 0))
    //   .forEach(metric => {
    //     const percentage = ((metric.duration || 0) / total * 100).toFixed(1);
    //     console.log(`  ${metric.name}: ${metric.duration?.toFixed(2)}ms (${percentage}%)`);
    //   });
    // console.log(`  Total: ${total.toFixed(2)}ms`);
    // console.groupEnd();
  }

  // Component render tracking
  trackRender(componentName: string): void {
    if (!this.enabled) return;
    // console.log(`🔄 [RENDER] ${componentName}`);
  }

  // API call tracking with enhanced metrics
  async trackAPI<T>(endpoint: string, fn: () => Promise<T>): Promise<T> {
    const startTime = performance.now();
    const metricName = `API: ${endpoint}`;

    try {
      const result = await this.measureAsync(metricName, fn);

      // Track successful API calls
      if (this.enabled) {
        const duration = performance.now() - startTime;
        this.reportToErrorTracker('api_call_success', duration, {
          endpoint,
          status: 'success'
        });
      }

      return result;
    } catch (error) {
      // Track failed API calls
      if (this.enabled) {
        const duration = performance.now() - startTime;
        this.reportToErrorTracker('api_call_error', duration, {
          endpoint,
          status: 'error',
          error: error instanceof Error ? error.message : String(error)
        });
      }

      throw error;
    }
  }

  // Enhanced component render tracking
  trackComponentRender(componentName: string, renderTime?: number) {
    if (!this.enabled) return;

    const time = renderTime || performance.now();
    // console.log(`🔄 [RENDER] ${componentName} at ${time.toFixed(2)}ms`);

    this.reportToErrorTracker('component_render', time, {
      componentName
    });
  }

  // Track Core Web Vitals
  trackWebVitals() {
    if (!this.enabled || typeof window === 'undefined') return;

    // First Input Delay (FID)
    try {
      new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.entryType === 'first-input') {
            const fidEntry = entry as any; // PerformanceEventTiming not available in all browsers
            const fid = fidEntry.processingStart - fidEntry.startTime;
            this.reportToErrorTracker('first_input_delay', fid);
          }
        });
      }).observe({ entryTypes: ['first-input'] });
    } catch (e) {
      // console.log('First Input Delay observation not supported');
    }

    // Largest Contentful Paint (LCP)
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (lastEntry) {
        this.reportToErrorTracker('largest_contentful_paint', lastEntry.startTime);
      }
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // Cumulative Layout Shift (CLS)
    let clsValue = 0;
    new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (!(entry as any).hadRecentInput) {
          clsValue += (entry as any).value;
        }
      });
      this.reportToErrorTracker('cumulative_layout_shift', clsValue);
    }).observe({ entryTypes: ['layout-shift'] });
  }

  // Track navigation timing
  trackNavigationTiming() {
    if (!this.enabled || typeof window === 'undefined') return;

    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      if (navigation) {
        this.reportToErrorTracker('dns_lookup', navigation.domainLookupEnd - navigation.domainLookupStart);
        this.reportToErrorTracker('tcp_connection', navigation.connectEnd - navigation.connectStart);
        this.reportToErrorTracker('server_response', navigation.responseStart - navigation.requestStart);
        this.reportToErrorTracker('dom_content_loaded', navigation.domContentLoadedEventEnd - navigation.fetchStart);
        this.reportToErrorTracker('page_load', navigation.loadEventEnd - navigation.fetchStart);
      }
    });
  }

  // Report metrics to error tracker (if available)
  private reportToErrorTracker(metric: string, value: number, additionalData?: Record<string, any>) {
    if (typeof window !== 'undefined' && (window as any).errorTracker) {
      (window as any).errorTracker.reportPerformanceMetric(metric, value, additionalData);
    }
  }
}

// Export singleton instance
export const perfMonitor = new PerformanceMonitor();

// React hook for component performance tracking
export const usePerformanceTracking = (componentName: string) => {
  // Performance tracking disabled
  // if (process.env.NODE_ENV === 'development') {
  //   perfMonitor.trackRender(componentName);
  // }
};